import * as state from './state.js';

export function clampPaneSplitRatio(ratio) {
    if (!Number.isFinite(ratio)) {
        return state.PANE_SPLIT_DEFAULT;
    }
    return Math.min(state.PANE_SPLIT_MAX, Math.max(state.PANE_SPLIT_MIN, ratio));
}

export function paneSplitRatioFromPointer(containerRect, clientX) {
    const width = containerRect.width;
    if (width <= 0) {
        return state.PANE_SPLIT_DEFAULT;
    }
    return clampPaneSplitRatio((clientX - containerRect.left) / width);
}

export function getPaneResizeHandleWidth() {
    const handle = document.getElementById('pane-resize-handle');
    return handle ? handle.offsetWidth : 6;
}

export function maxEditorPaneWidth(containerWidth) {
    return Math.max(0, containerWidth - getPaneResizeHandleWidth());
}

export function paneSplitRatioForPreviewWidth(previewWidthPx, containerWidth, handleWidth = getPaneResizeHandleWidth()) {
    if (containerWidth <= 0 || !Number.isFinite(previewWidthPx)) {
        return state.PANE_SPLIT_DEFAULT;
    }
    const editorWidth = containerWidth - handleWidth - previewWidthPx;
    return clampPaneSplitRatio(editorWidth / containerWidth);
}

export function snapPreviewPaneWidth(previewWidthPx) {
    const container = document.querySelector('.playground-container');
    if (!container) {
        return;
    }
    const width = container.getBoundingClientRect().width;
    const ratio = paneSplitRatioForPreviewWidth(previewWidthPx, width);
    applyPaneSplitRatio(ratio);
    window.localStorage.setItem(state.PANE_SPLIT_STORAGE_KEY, String(ratio));
}

export function editorPaneWidthFromRatio(ratio, containerWidth) {
    const min = containerWidth * state.PANE_SPLIT_MIN;
    const max = Math.min(containerWidth * state.PANE_SPLIT_MAX, maxEditorPaneWidth(containerWidth));
    const target = containerWidth * clampPaneSplitRatio(ratio);
    return Math.min(Math.max(min, target), max);
}

export function applyPaneSplitRatio(ratio) {
    const container = document.querySelector('.playground-container');
    if (!container) {
        return;
    }
    const width = container.getBoundingClientRect().width;
    const editorWidth = editorPaneWidthFromRatio(ratio, width);
    document.documentElement.style.setProperty('--editor-pane-width', `${Math.round(editorWidth)}px`);
    document.body.classList.add('custom-pane-split');
    const handle = document.getElementById('pane-resize-handle');
    if (handle) {
        const percent = Math.round(clampPaneSplitRatio(ratio) * 100);
        handle.setAttribute('aria-valuenow', String(percent));
    }
}

export function getTerminalCharProbe() {
    if (!state.terminalCharProbe) {
        const probe = document.createElement('pre');
        probe.id = 'terminal-char-probe';
        probe.setAttribute('aria-hidden', 'true');
        probe.style.cssText = [
            'position:fixed',
            'left:-9999px',
            'top:0',
            'visibility:hidden',
            'margin:0',
            'padding:0',
            'border:0',
            'white-space:pre',
            `font:12px/1.25 ${state.TERMINAL_FONT_STACK}`,
            'letter-spacing:0',
        ].join(';');
        probe.textContent = 'M';
        document.body.appendChild(probe);
        state.setTerminalCharProbe(probe);
    }
    return state.terminalCharProbe;
}

export function measurePlaygroundTerminalSize() {
    const previewContent = document.getElementById('preview-content');
    if (!previewContent) {
        return { width: 120, height: 40 };
    }

    const probeRect = getTerminalCharProbe().getBoundingClientRect();
    const charWidth = probeRect.width > 0 ? probeRect.width : 7.2;
    const lineHeight = probeRect.height > 0 ? probeRect.height : 15;

    const innerWidth = previewContent.clientWidth - state.TERMINAL_PREVIEW_PAD_X - state.TERMINAL_BOX_PAD_X;
    const innerHeight = previewContent.clientHeight - state.TERMINAL_PREVIEW_PAD_Y - state.TERMINAL_BOX_PAD_Y;
    const fallbackHeight = Math.max(0, window.innerHeight - 200);

    const width = Math.max(
        state.TERMINAL_MIN_COLS,
        Math.min(state.TERMINAL_MAX_COLS, Math.floor(Math.max(innerWidth, 0) / charWidth)),
    );
    const height = Math.max(
        state.TERMINAL_MIN_ROWS,
        Math.min(
            state.TERMINAL_MAX_ROWS,
            Math.floor(Math.max(innerHeight > 0 ? innerHeight : fallbackHeight, 0) / lineHeight),
        ),
    );
    return { width, height };
}

export function attachTerminalSize(payload, format) {
    if (format !== 'terminal') {
        return payload;
    }
    const size = measurePlaygroundTerminalSize();
    payload.terminal_width = size.width;
    payload.terminal_height = size.height;
    return payload;
}

export function scheduleTerminalRerender(sendYAMLToPreviewFn) {
    const format = document.getElementById('format-selector')?.value;
    if (format !== 'terminal' || !state.editor) {
        return;
    }
    clearTimeout(state.terminalRerenderTimeout);
    state.setTerminalRerenderTimeout(setTimeout(() => {
        sendYAMLToPreviewFn();
    }, 150));
}

export function refreshPaneLayout(sendYAMLToPreviewFn, scalePreviewContentFn, resizeSvgIframeFn) {
    if (!document.body.classList.contains('custom-pane-split')) {
        scheduleTerminalRerender(sendYAMLToPreviewFn);
        return;
    }
    const raw = window.localStorage.getItem(state.PANE_SPLIT_STORAGE_KEY);
    const ratio = raw == null ? state.PANE_SPLIT_DEFAULT : clampPaneSplitRatio(parseFloat(raw));
    applyPaneSplitRatio(ratio);
    if (state.editor) {
        state.editor.requestMeasure();
    }
    if (state.codePreview && state.codePreview.dom.offsetParent !== null) {
        state.codePreview.requestMeasure();
    }
    scalePreviewContentFn();
    resizeSvgIframeFn();
    scheduleTerminalRerender(sendYAMLToPreviewFn);
}

export function initPaneResize(sendYAMLToPreviewFn, scalePreviewContentFn, resizeSvgIframeFn) {
    const handle = document.getElementById('pane-resize-handle');
    const container = document.querySelector('.playground-container');
    if (!handle || !container) {
        return;
    }

    const mobileQuery = window.matchMedia('(max-width: 768px)');
    let activePointerId = null;
    let dragRatio = state.PANE_SPLIT_DEFAULT;

    function isResizeEnabled() {
        return !mobileQuery.matches && !document.body.classList.contains('fullscreen-mode');
    }

    function finishDrag(pointerId) {
        if (activePointerId !== pointerId) {
            return;
        }
        if (handle.hasPointerCapture(pointerId)) {
            handle.releasePointerCapture(pointerId);
        }
        window.localStorage.setItem(state.PANE_SPLIT_STORAGE_KEY, String(dragRatio));
        activePointerId = null;
        document.body.classList.remove('pane-resize-dragging');
        refreshPaneLayout(sendYAMLToPreviewFn, scalePreviewContentFn, resizeSvgIframeFn);
    }

    handle.addEventListener('pointerdown', (event) => {
        if (!isResizeEnabled() || event.button !== 0) {
            return;
        }
        event.preventDefault();
        activePointerId = event.pointerId;
        handle.setPointerCapture(event.pointerId);
        document.body.classList.add('pane-resize-dragging');
        const rect = container.getBoundingClientRect();
        dragRatio = paneSplitRatioFromPointer(rect, event.clientX);
        applyPaneSplitRatio(dragRatio);
    });

    handle.addEventListener('pointermove', (event) => {
        if (activePointerId !== event.pointerId) {
            return;
        }
        const rect = container.getBoundingClientRect();
        dragRatio = paneSplitRatioFromPointer(rect, event.clientX);
        applyPaneSplitRatio(dragRatio);
    });

    handle.addEventListener('pointerup', (event) => finishDrag(event.pointerId));
    handle.addEventListener('pointercancel', (event) => finishDrag(event.pointerId));

    handle.addEventListener('keydown', (event) => {
        if (!isResizeEnabled()) {
            return;
        }
        let delta;
        if (event.key === 'ArrowLeft') {
            delta = -0.02;
        } else if (event.key === 'ArrowRight') {
            delta = 0.02;
        } else {
            return;
        }
        event.preventDefault();
        const raw = window.localStorage.getItem(state.PANE_SPLIT_STORAGE_KEY);
        const current = raw == null ? state.PANE_SPLIT_DEFAULT : clampPaneSplitRatio(parseFloat(raw));
        const next = clampPaneSplitRatio(current + delta);
        applyPaneSplitRatio(next);
        window.localStorage.setItem(state.PANE_SPLIT_STORAGE_KEY, String(next));
        refreshPaneLayout(sendYAMLToPreviewFn, scalePreviewContentFn, resizeSvgIframeFn);
    });

    const saved = window.localStorage.getItem(state.PANE_SPLIT_STORAGE_KEY);
    if (saved != null) {
        applyPaneSplitRatio(clampPaneSplitRatio(parseFloat(saved)));
    }

    // Keep the Width input synced with the actual preview pane width.
    // ResizeObserver covers drag, window resize, fullscreen, and programmatic
    // snap in one path. We seed the initial value before installing the observer
    // because the observer's initial fire isn't guaranteed before paint, and the
    // Width input would otherwise read blank on first render. Skip the write
    // while the input is focused so we don't clobber what the user is typing.
    const previewPane = document.querySelector('.preview-pane');
    const widthInput = document.getElementById('review-width-input');
    const writePreviewWidth = (px) => {
        if (!widthInput || widthInput === document.activeElement) return;
        widthInput.value = String(Math.round(px));
    };
    if (previewPane && widthInput) {
        writePreviewWidth(previewPane.getBoundingClientRect().width);
        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver((entries) => {
                const w = entries[0]?.contentRect?.width;
                if (w) writePreviewWidth(w);
            });
            observer.observe(previewPane);
        }
    }

    let _layoutTimeout = null;
    window.addEventListener('resize', () => {
        clearTimeout(_layoutTimeout);
        _layoutTimeout = setTimeout(() => refreshPaneLayout(sendYAMLToPreviewFn, scalePreviewContentFn, resizeSvgIframeFn), 100);
    });

    document.addEventListener('fullscreenchange', () => {
        handle.tabIndex = isResizeEnabled() ? 0 : -1;
        refreshPaneLayout(sendYAMLToPreviewFn, scalePreviewContentFn, resizeSvgIframeFn);
    });

    mobileQuery.addEventListener('change', () => refreshPaneLayout(sendYAMLToPreviewFn, scalePreviewContentFn, resizeSvgIframeFn));
}
